<script setup>
import { ref, computed, watch } from 'vue'
import { useGlobalStore } from '@stores/global.js'
import { useProjectStore } from '@stores/project.js'
import Input from '@components/global/forms/Input.vue'
import Toggle from '@components/global/forms/Toggle.vue'
import Select from '@components/global/forms/Select.vue'
import ProjectCard from '@components/work/ProjectCard.vue'
import TransitionFade from '@components/global/transitions/TransitionFade.vue'
import TransitionNumber from '@components/global/transitions/TransitionNumber.vue'
import Button from '@components/global/forms/Button.vue'

const global = useGlobalStore()
const projectStore = useProjectStore()

const layouts = ref([
	{
		name: 'tile',
		icon: 'far fa-grid-2',
		title: 'Tile View',
	},
	{
		name: 'list',
		icon: 'fas fa-bars',
		title: 'List View',
	},
])

const projectsWrapper = ref()
const layout = ref('tile')
const filters = ref({})

const filteredProjects = computed(() => {
	return projectStore.projects.filter((project) => {
		const categoryMatch = !filters.value.category || filters.value.category.length === 0 || project.categories.some((cat) => filters.value.category.includes(cat.slug))
		const industryMatch = !filters.value.industry || filters.value.industry.length === 0 || project.industries.some((ind) => filters.value.industry.includes(ind.slug))
		return categoryMatch && industryMatch
	})
})

const resetFilters = () => {
	filters.value = {}
}

const resetScroll = () => {
	const delta = projectsWrapper.value.getBoundingClientRect().top + window.scrollY - (global.screenWidth < 768 ? 265 : global.screenWidth < 1024 ? 215 : 175)
	window.scrollTo({ top: delta, behavior: 'smooth' })
}

watch(filters.value, () => {
	resetScroll()
})
</script>

<template>
	<section class="relative bg-crowns-off-black pb-16" v-if="projectStore.projects?.length">
		<div class="container">
			<div id="projects-header" class="sticky top-0 z-20 mb-5 flex flex-col gap-1 bg-crowns-off-black pb-2 pt-20 md:gap-2 md:pb-4 md:pt-24 lg:flex-row lg:gap-8">
				<div class="flex items-center justify-between gap-2">
					<div class="flex items-center gap-2">
						<h5>Projects</h5>
						<div class="flex size-8 items-center justify-center rounded-full bg-crowns-off-white text-lg font-semibold text-crowns-off-black lg:size-10 lg:text-xl"><TransitionNumber :number="filteredProjects.length" /></div>
					</div>
					<Toggle class="md:hidden" :options="layouts" v-model="layout" @click="resetScroll()" />
				</div>
				<div class="border-r border-crowns-grey"></div>
				<div class="flex flex-col gap-2 whitespace-nowrap text-white sm:flex-row sm:items-center sm:gap-3">
					<div class="-mb-1 mr-3 whitespace-nowrap text-sm font-light text-crowns-clouds sm:mb-0 sm:text-crowns-off-white">Filter By</div>
					<Select class="w-full" placeholder="Services" :options="projectStore.categories" v-model="filters.category" />
					<Select class="w-full" placeholder="Industry" :options="projectStore.industries" v-model="filters.industry" />
					<Toggle class="hidden md:flex" :options="layouts" v-model="layout" @click="resetScroll()" />
				</div>
			</div>
			<div ref="projectsWrapper">
				<TransitionFade>
					<!-- Grid View -->
					<TransitionGroup name="grid" tag="div" :class="[{ 'grid grid-cols-2 gap-5 lg:grid-cols-3 2xl:grid-cols-4': layout == 'tile' }]" v-if="layout == 'tile'">
						<ProjectCard v-for="project in filteredProjects" :type="layout" :project="project" :key="project.slug" />
					</TransitionGroup>
					<!-- List View -->
					<div class="flex flex-col gap-16" v-else>
						<ProjectCard v-for="project in filteredProjects" :type="layout" :project="project" />
					</div>
				</TransitionFade>
				<!-- No Projects -->
				<div class="flex h-72 flex-col items-center justify-center gap-4" v-if="filteredProjects.length === 0">
					<!-- <h6>Whoops</h6> -->
					<p class="text-center">We only showcase a small percentage of our work on our website, please contact us with your specific project needs and we will likely have relevent work we can share.</p>
					<div class="flex gap-2">
						<Button type="underline" label="Reset Filters" @click="resetFilters()" />
						<Button type="underline" label="Contact Us" :to="{ name: 'contact' }" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped>
.grid-leave-active {
	@apply absolute;
}

#projects-header:before {
	content: '';
	position: absolute;
	top: 0;
	left: -2.5%;
	width: 105%;
	height: 100%;
	background-color: #080808;
	z-index: -1;
}
</style>
